import { useCallback, useEffect, useRef } from 'react';

const DWELL_FLUSH_MS = 15000;

function postEngagement(offerId, token, body, useBeacon) {
  const payload = JSON.stringify({ token, ...body });
  const url = `/api/offers/${offerId}/engagement`;

  if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    try {
      const blob = new Blob([payload], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return;
    } catch (err) {
      // Fall through to fetch when the beacon cannot be queued.
    }
  }

  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true
  }).catch(() => {});
}

function isInViewport(node) {
  const rect = node.getBoundingClientRect();
  const viewHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < viewHeight;
}

/**
 * Records that the customer read the Anfrage and how long the summary stayed visible.
 * Read fires once when the sentinel enters the viewport. Dwell flushes while the tab is visible.
 *
 * @param {{ offerId?: string, token?: string, active?: boolean }} options
 * @returns {(node: HTMLElement|null) => void}
 */
export default function useOfferEngagement({ offerId, token, active }) {
  const readSentRef = useRef(false);
  const disconnectReadRef = useRef(() => {});
  const offerKeyRef = useRef(offerId);
  if (offerKeyRef.current !== offerId) {
    offerKeyRef.current = offerId;
    readSentRef.current = false;
  }

  const markRead = useCallback(() => {
    if (!active || !offerId || !token || readSentRef.current) return;
    readSentRef.current = true;
    disconnectReadRef.current();
    postEngagement(offerId, token, { event: 'read' }, false);
  }, [active, offerId, token]);

  const sentinelRef = useCallback((node) => {
    disconnectReadRef.current();
    if (!node || !active || !offerId || !token || readSentRef.current) return;

    let observer = null;
    let frame = 0;

    const stop = () => {
      if (frame) cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
    };
    disconnectReadRef.current = stop;

    if (typeof IntersectionObserver === 'function') {
      observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) markRead();
      }, { threshold: 0 });
      observer.observe(node);
    }

    frame = requestAnimationFrame(() => {
      if (isInViewport(node)) markRead();
    });
  }, [active, offerId, token, markRead]);

  useEffect(() => () => disconnectReadRef.current(), [sentinelRef]);

  useEffect(() => {
    if (!active || !offerId || !token) return undefined;

    let unsentMs = 0;
    let tickStart = document.visibilityState === 'visible' ? Date.now() : null;

    const collect = () => {
      if (tickStart != null) {
        unsentMs += Date.now() - tickStart;
        tickStart = document.visibilityState === 'visible' ? Date.now() : null;
      }
    };

    const flush = (useBeacon) => {
      collect();
      const deltaMs = unsentMs;
      unsentMs = 0;
      if (deltaMs < 1) return;
      postEngagement(offerId, token, { event: 'dwell', deltaMs }, useBeacon);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        flush(true);
        tickStart = null;
      } else if (tickStart == null) {
        tickStart = Date.now();
      }
    };

    const onPageHide = () => flush(true);

    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') flush(false);
    }, DWELL_FLUSH_MS);

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      flush(true);
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [active, offerId, token]);

  return sentinelRef;
}
